import TextField from '@mui/material/TextField';
import type { DraggableProvidedDragHandleProps } from 'react-beautiful-dnd';
import { EyeOff, Trash2 } from 'react-feather';

import { QueryResponseType } from '@/__generated__/graphql';
import Checkbox from '@/componentsv2/Checkbox';
import FAIcon from '@/componentsv2/Icon/FAIcon';
import type { QueryRowPerm } from '@/expertrequest/utils/fieldPermissions';

import { type QueryAction, QueryActionsMenu } from './QueryActionsMenu';
import { ResponseTypeSelect } from './ResponseTypeSelect';

interface VettingQueryCardProps {
  id: string;
  noun: string;
  perm: QueryRowPerm;
  /** Whether the row exists server-side (has an id). Hide is only valid on persisted rows. */
  isPersisted: boolean;
  text: string;
  required: boolean;
  responseType: QueryResponseType;
  placeholder?: string;
  error?: string;
  dragHandleProps?: DraggableProvidedDragHandleProps | null;
  onText: (text: string) => void;
  onRequired: (required: boolean) => void;
  onResponseType: (value: QueryResponseType) => void;
  onHide: () => void;
  onDelete: () => void;
}

/**
 * One vetting question/qualification row (ENG-5292). Presentational/controlled:
 * the form layer supplies values, callbacks, the drag handle, and the per-row
 * `perm` derived from `field_permissions`. `read` rows have no footer/menu.
 */
export function VettingQueryCard({
  id,
  noun,
  perm,
  isPersisted,
  text,
  required,
  responseType,
  placeholder,
  error,
  dragHandleProps,
  onText,
  onRequired,
  onResponseType,
  onHide,
  onDelete,
}: VettingQueryCardProps): JSX.Element {
  const editable = perm === 'edit';
  const readOnly = perm === 'read';

  const actions: QueryAction[] = [];
  // Hide requires a persisted row (the mutation needs an id) and a stage that
  // permits hiding; new/unsaved rows can only be edited or deleted.
  if (isPersisted && (perm === 'edit' || perm === 'hide_reorder')) {
    actions.push({
      key: 'hide',
      icon: EyeOff,
      label: 'Hide from experts',
      tone: 'brand',
      onClick: onHide,
    });
  }
  if (editable) {
    actions.push({
      key: 'delete',
      icon: Trash2,
      label: `Delete ${noun}`,
      tone: 'destructive',
      onClick: onDelete,
    });
  }

  return (
    <div
      className={
        readOnly
          ? 'relative'
          : 'relative rounded-sm border border-surface-border bg-surface-card p-14'
      }
    >
      <div className={`flex gap-10 ${editable ? 'items-start' : 'items-center'}`}>
        {dragHandleProps && (
          <span
            {...dragHandleProps}
            aria-label="Drag to reorder"
            className="flex h-40 w-24 shrink-0 cursor-grab items-center justify-center text-on-surface-subtle"
          >
            <FAIcon icon="bars" />
          </span>
        )}
        <div className="min-w-0 flex-1">
          {editable ? (
            // Same MUI outlined multiline field as the form's other textareas
            // ("Describe Your Company", "Ideal Expert Profile"): native notched
            // label + border, auto-resizing between minRows/maxRows (no drag handle).
            <TextField
              label={`Add ${noun}`}
              value={text}
              onChange={(e) => onText(e.target.value)}
              placeholder={placeholder ?? `Type your ${noun}…`}
              variant="outlined"
              margin="none"
              fullWidth
              multiline
              minRows={2}
              maxRows={50}
              error={!!error}
              inputProps={{ maxLength: 2048 }}
              sx={{ '& .MuiInputLabel-root': { textTransform: 'capitalize' } }}
            />
          ) : (
            <div className="text-14 leading-normal text-on-surface-secondary">{text || '—'}</div>
          )}
          {error && <span className="mt-4 block text-12 text-alert-error">{error}</span>}
        </div>
        {actions.length > 0 && <QueryActionsMenu id={`query-actions-${id}`} actions={actions} />}
      </div>
      {!readOnly && (
        <div
          className={`mt-12 flex flex-wrap items-center gap-x-8 gap-y-8 sm:gap-x-12 ${
            dragHandleProps ? 'sm:pl-32' : ''
          }`}
        >
          <Checkbox
            checked={required}
            disabled={!editable}
            onChange={(_, checked) => onRequired(checked)}
            label={
              <span className="text-12 font-semibold text-on-surface-secondary sm:text-14">
                Required
              </span>
            }
            size="small"
          />
          <ResponseTypeSelect
            id={`response-type-${id}`}
            value={responseType}
            readOnly={!editable}
            onChange={onResponseType}
          />
        </div>
      )}
    </div>
  );
}
